import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// Actions
import {
  getZones,
  selectZone,
  unSelectZone,
} from "../../actions/campaign-planning/ZonesAndSubZonesActions";

// Utils and Constants
import { getUpdatedCityName } from "../../../common-utils/city-utils/CityUtils";

// Components
import CheckBox from "../../../components/check-box/CheckBox";
import { DataLoading, HeaderWithActionButton } from "./PlanningUtils";

// Zones
function ZoneList({
  zones = [],
  selectedRegionId = "",
  selectedRegionName = "",
  selectedRegionAndZonesMap = {},
  appliedFiltersDetailsMap = {},
}) {
  const dispatch = useDispatch();

  // checking zones present
  const isZonesPresent = zones.length > 0;

  // zones for selected region
  const selectedZones = selectedRegionAndZonesMap[selectedRegionId] || [];
  const selectedZoneIds = selectedZones.map((zone) => zone.zoneId);

  const {
    appliedFilterOrder = {},
    isMediaTypesApplied = false,
    isArterialRoutesApplied = false,
    firstAppliedFilterOptions = {},
    secondAppliedFilterOptions = {},
  } = appliedFiltersDetailsMap || {};
  const isFilteredOption = isMediaTypesApplied || isArterialRoutesApplied;
  const { zoneIds: firstZoneIds = [] } = firstAppliedFilterOptions || {};
  const { zoneIds: secondZoneIds = [] } = secondAppliedFilterOptions || {};
  const isZoneFirstApplied = appliedFilterOrder?.firstApplied === "zone";
  const isZoneSecondApplied = appliedFilterOrder?.secondApplied === "zone";

  const isFirstAppliedPresent = !!appliedFilterOrder?.firstApplied;
  const isSecondAppliedPresent = !!appliedFilterOrder?.secondApplied;

  // function
  function handleSelectZone(e, zoneId) {
    const { checked } = e.target || {};

    // find selected Zone
    const selectedZone = zones.find((zone) => zone.id === zoneId) || {};

    // construct Zone Object
    const constructedZone = {
      cityId: selectedRegionId,
      cityName: selectedRegionName,
      zoneId: zoneId,
      zoneName: selectedZone?.name || "",
    };

    // dispatch to select/unselect
    const dispatchFn = checked ? selectZone : unSelectZone;
    dispatch(dispatchFn(constructedZone));
  }

  return (
    <div className="col-4 px-0">
      <h5 className="my-1 px-3 py-2">Zone</h5>
      <hr className="my-0" />

      {/* no zones */}
      {!isZonesPresent && (
        <p className="text-center font-italic text-muted my-2 px-3 py-2">
          No Zone Available
        </p>
      )}

      {/* zones list */}
      {isZonesPresent &&
        zones.map((zone) => {
          const { name, id } = zone;
          const isSelected = selectedZoneIds.includes(id);

          // logic to filter options
          if (isFilteredOption && !isZoneFirstApplied && !isSelected) {
            if (isSecondAppliedPresent) {
              if (isZoneSecondApplied) {
                if (!firstZoneIds.includes(id)) {
                  return;
                }
              }

              if (!isZoneSecondApplied) {
                if (!secondZoneIds.includes(id)) {
                  return;
                }
              }
            }

            if (isFirstAppliedPresent && !isZoneFirstApplied) {
              if (!firstZoneIds.includes(id)) {
                return;
              }
            }
          }

          return (
            <div
              key={id}
              className="d-flex justify-content-between mt-1 px-3 py-2"
            >
              <p className="mb-0 text-truncate-2" title={name}>
                {name}
              </p>
              <CheckBox
                boxStyle="mb-0 ml-2"
                inputBoxStyle="m-0"
                checkBoxKey={id}
                showLabel={false}
                checked={isSelected}
                onChange={handleSelectZone}
              />
            </div>
          );
        })}
    </div>
  );
}

// Regions
function RegionList({
  regionList = [],
  selectedRegionId,
  setSelectedRegionId = () => {},
  setSelectedRegionName = () => {},
  selectedRegionAndZonesMap = {},
}) {
  const dispatch = useDispatch();

  function handleSelectRegion(id, name) {
    setSelectedRegionId(id);
    setSelectedRegionName(name);

    // get zones
    dispatch(getZones(id));
  }

  return (
    <div className="col-4 px-0 border-right">
      <h5 className="my-1 px-3 py-2">Region</h5>
      <hr className="my-0" />

      <div className="px-3">
        {regionList.map((region) => {
          const { name, id } = region;
          const updatedName = getUpdatedCityName(id, name);
          const isRegionSelected = selectedRegionId === id;
          const selectedBgStyle = isRegionSelected ? "bg-alt rounded-lg" : "";
          const selectedZones = selectedRegionAndZonesMap[id] || [];
          const iconClass = isRegionSelected
            ? "fa-angle-down text-primary"
            : "fa-angle-right";
          return (
            <div
              key={id}
              className={`d-flex align-items-center justify-content-between p-2 cursor-pointer my-2 ${selectedBgStyle}`}
              onClick={() => handleSelectRegion(id, updatedName)}
            >
              <p className="mb-0 text-truncate-2" title={updatedName}>
                {updatedName}
              </p>
              <p className="mb-0">
                {selectedZones.length > 0 && (
                  <span className="mr-2">({selectedZones.length})</span>
                )}
                <i className={`fa ${iconClass}`} />
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// get District Wise ZoneMap
function getDistrictWiseZoneMap(zoneList = []) {
  // group by district
  const districtMap = zoneList.reduce((acc, zone) => {
    const { district } = zone;

    // add district key
    if (!acc[district]) {
      acc[district] = [];
    }

    // push the zone to district
    acc[district].push(zone);
    return acc;
  }, {});

  // return map
  return districtMap;
}

// get District Selected Zones
function getDistrictSelectedZones(districtZones, selectedZones) {
  if (districtZones.length === 0) {
    return { districtSelectedZones: [], districtSelectedZonesCount: 0 };
  }

  // filter by matching Id
  const districtSelectedZones = districtZones.filter((zone) =>
    selectedZones.some((selected) => selected.zoneId === zone.id)
  );

  // count
  const districtSelectedZonesCount = districtSelectedZones.length;

  return { districtSelectedZones, districtSelectedZonesCount };
}

// District List
function DistrictList({
  zones = [],
  selectedRegionId,
  zonesLoading = false,
  selectedDistrict = "",
  setSelectedDistrict = () => {},
  setSelectedDistrictZones = () => {},
  selectedRegionAndZonesMap = {},
}) {
  // district wise list of zones
  const districtWiseZoneMap = getDistrictWiseZoneMap(zones);
  const districtKeys = Object.keys(districtWiseZoneMap);
  const isDistrictPresent = Object.keys(districtKeys).length > 0;

  // selected zones
  const selectedZones = selectedRegionAndZonesMap[selectedRegionId] || [];

  // function
  function handleSelectDistrict(districtKey, districtZones) {
    // update selected district
    setSelectedDistrict(districtKey);

    // update selected district zones
    setSelectedDistrictZones(districtZones);
  }

  return (
    <div className="col-4 px-0 border-right">
      <h5 className="my-1 px-3 py-2">City</h5>
      <hr className="my-0" />

      <div className="px-3">
        {/* zones loading */}
        {zonesLoading && (
          <DataLoading
            className="my-2 px-3 py-2"
            spinnerClassName="spinner-border-sm"
          />
        )}

        {/* no data */}
        {!zonesLoading && !isDistrictPresent && (
          <p className="p-3">No City Present</p>
        )}

        {/* cities */}
        {isDistrictPresent &&
          districtKeys.map((districtKey) => {
            const isSelectedDistrict = selectedDistrict === districtKey;

            // Count matching zones
            const districtZones = districtWiseZoneMap[districtKey] || [];
            const { districtSelectedZonesCount } = getDistrictSelectedZones(
              districtZones,
              selectedZones
            );

            const selectedBgStyle = isSelectedDistrict
              ? "bg-alt rounded-lg"
              : "";
            const iconClass = isSelectedDistrict
              ? "fa-angle-down text-primary"
              : "fa-angle-right";

            return (
              <div
                key={districtKey}
                className={`d-flex align-items-center justify-content-between p-2 cursor-pointer my-2 ${selectedBgStyle}`}
                onClick={() => handleSelectDistrict(districtKey, districtZones)}
              >
                <p className="mb-0 text-truncate-2" title={districtKey}>
                  {districtKey}
                </p>
                <p className="mb-0 ml-1">
                  {districtSelectedZonesCount > 0 && (
                    <span className="mr-2">({districtSelectedZonesCount})</span>
                  )}
                  <i className={`fa ${iconClass}`} />
                </p>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// Regions and Zones List
function RegionsAndZonesList({
  regionList = [],
  regionListLoading = false,
  zones = [],
  zonesLoading = false,
  selectedRegionAndZonesMap = {},
  appliedFiltersDetailsMap = {},
}) {
  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [selectedRegionName, setSelectedRegionName] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedDistrictZones, setSelectedDistrictZones] = useState([]);

  // loading
  if (regionListLoading) {
    return <DataLoading dataType="City / Zone" />;
  }

  // no data msg
  if (regionList.length === 0) {
    return (
      <p className="text-center font-italic text-muted">
        No City / Zone Available
      </p>
    );
  }

  return (
    <div className="w-100 d-flex border rounded-lg overflow-hidden">
      <RegionList
        regionList={regionList}
        selectedRegionId={selectedRegionId}
        setSelectedRegionId={setSelectedRegionId}
        setSelectedRegionName={setSelectedRegionName}
        selectedRegionAndZonesMap={selectedRegionAndZonesMap}
      />

      {/* districts */}
      {selectedRegionId && (
        <DistrictList
          zones={zones}
          selectedRegionId={selectedRegionId}
          zonesLoading={zonesLoading}
          selectedDistrict={selectedDistrict}
          setSelectedDistrict={setSelectedDistrict}
          setSelectedDistrictZones={setSelectedDistrictZones}
          selectedRegionAndZonesMap={selectedRegionAndZonesMap}
        />
      )}

      {/* zones */}
      {selectedRegionId && selectedDistrict && !zonesLoading && (
        <ZoneList
          zones={selectedDistrictZones}
          selectedRegionId={selectedRegionId}
          selectedRegionName={selectedRegionName}
          selectedRegionAndZonesMap={selectedRegionAndZonesMap}
          appliedFiltersDetailsMap={appliedFiltersDetailsMap}
        />
      )}
    </div>
  );
}

export default function ZonesTab({
  selectedCityId = "",
  handleTabHeaderBtnClick = () => {},
  tabHeaderBtnLoading = false,
  appliedFiltersDetailsMap = {},
}) {
  // region list
  const regionList = useSelector((state) => state.region.regionList);
  const regionListLoading = useSelector(
    (state) => state.region.regionListLoading
  );

  // Zones
  const zones = useSelector((state) => state.zonesAndSubZones.zones);
  const zonesLoading = useSelector(
    (state) => state.zonesAndSubZones.zonesLoading
  );

  // selected Region & Zones Map
  const selectedRegionAndZonesMap = useSelector(
    (state) => state.zonesAndSubZones.selectedRegionAndZonesMap
  );

  // find selected region
  const selectedRegion =
    regionList.find((eachRegion) => eachRegion.id === selectedCityId) || {};

  return (
    <>
      <HeaderWithActionButton
        title="City / Zone"
        buttonText="Confirm"
        onButtonClick={handleTabHeaderBtnClick}
        buttonLoading={tabHeaderBtnLoading}
      />

      {/* Regions and Zones List */}
      <RegionsAndZonesList
        regionList={[selectedRegion]}
        regionListLoading={regionListLoading}
        zones={zones}
        zonesLoading={zonesLoading}
        selectedRegionAndZonesMap={selectedRegionAndZonesMap}
        appliedFiltersDetailsMap={appliedFiltersDetailsMap}
      />
    </>
  );
}
